import { useEffect, useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { EMAIL_REGEX } from "@memento/library/email";
import { useReCaptcha } from "@wojtekmaj/react-recaptcha-v3";
import { useAuth } from "src/hooks/useAuth";
import { toastError } from "src/libs/toast-lib";
import { Button } from "@memento/ui/Button";
import { LoadingButton } from "@memento/ui/LoadingButton";
import { LabeledInput } from "@memento/ui/Form/LabeledInput";
import { Modal } from "src/components/Elements/Modal";
import { useForm } from "react-hook-form";
import { ProviderNameCognito } from "src/libs/user-lib";

type ResendFormValues = {
  email: string;
};

const schema = z.object({
  email: z
    .string()
    .regex(EMAIL_REGEX, { message: "Please provide a valid email address." })
    .max(1000)
    .nonempty({ message: "Please enter your email address." }),
});

type ResendVerifyModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  email?: string;
  message?: string;
  provider?: ProviderNameCognito;
  title?: string;
  onComplete: () => void;
};

export function ResendVerifyModal({
  isOpen,
  setIsOpen,
  email,
  message = "",
  provider,
  title = "We need to verify your email.",
  onComplete,
}: ResendVerifyModalProps) {
  const { sendVerification } = useAuth();
  const { executeRecaptcha } = useReCaptcha();
  const [loading, setLoading] = useState(false);
  const isForm = !email;

  const {
    register,
    getValues,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useForm<ResendFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: email || "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      clearErrors();
    }
  }, [isOpen, clearErrors]);

  const handleSend = async () => {
    const thisEmail = isForm ? getValues("email") : email;

    if (loading) {
      return;
    }

    setLoading(true);

    try {
      const captchaToken = executeRecaptcha
        ? (await executeRecaptcha("resend_verification")) || undefined
        : undefined;

      await sendVerification({ email: thisEmail, provider, captchaToken });
      setIsOpen(false);
      onComplete();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Something went wrong and we could not resend your verification email. Please try again.";

      toastError(message);
    }

    setLoading(false);
  };

  return (
    <Modal isOpen={isOpen} dismissable setIsOpen={setIsOpen}>
      <div className="max-w-xl">
        <h2 className="font-futura text-2xl">{title}</h2>

        <hr className="my-6" />
        {isForm ? (
          <LabeledInput
            label="Email"
            id="email"
            type="email"
            error={errors.email?.message}
            {...register("email")}
          />
        ) : (
          <p>
            {message ? message : null} Would you like to resend a verification
            link to <b className="font-semibold">{email}</b>?
          </p>
        )}
        <hr className="my-6" />
        <div className="mx-auto flex max-w-xs flex-col justify-between gap-3 sm:max-w-full sm:flex-row">
          <Button variant="inverse" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <LoadingButton
            onClick={() => (isForm ? handleSubmit(handleSend)() : handleSend())}
            isLoading={loading}
            text="Resend Verification Email"
            variant="primary"
            loadingText="Sending…"
          />
        </div>
      </div>
    </Modal>
  );
}
